import * as Yup from 'yup';

import {
    BusinessPourAvailability,
    BusinessPriceTier,
    MenuItem,
} from '@barboxx/models/types';
import { MENU_ITEM_TYPES, MenuItemType } from '@barboxx/types';
import { enumToList, transformEmptyToNull } from '@barboxx/utils';

export const BUSINESS_PRICE_TIER_SCHEMA = Yup.object<BusinessPriceTier.Base>()
    .shape({
        id: Yup.number().required(),
        businessId: Yup.number().required(),
        priceTierId: Yup.number().required(),
        pourTypeId: Yup.number().required(),
        productTypeId: Yup.number().nullable().default(null),
        productSubtypeId: Yup.number().nullable().default(null),
        isDefault: Yup.boolean().nullable().default(null),
        defaultUpCharge: Yup.number()
            .required()
            .when('isDefault', {
                is: (value: boolean | null) => value === true,
                then: schema => schema.equals([0]).default(0),
                otherwise: schema => schema.positive().required(),
            }),
        displayOrder: Yup.number().nullable().default(null),
    })
    .test(
        'one-product-assignment-scope',
        'Price tier can be assigned to either a product type or a product subtype, not both.',
        values => {
            if (!values) {
                return true;
            }

            return !(values.productTypeId && values.productSubtypeId);
        }
    );
export const CREATE_BUSINESS_PRICE_TIER_SCHEMA =
    BUSINESS_PRICE_TIER_SCHEMA.omit(['id']);

export const BUSINESS_POUR_AVAILABILITY_SCHEMA =
    Yup.object<BusinessPourAvailability.Base>().shape({
        id: Yup.number().required(),
        pourAvailabilityId: Yup.number().required(),
        businessId: Yup.number().required(),
        isActive: Yup.boolean().default(true),
        notAllowed: Yup.boolean().default(false),
        basePrice: Yup.number()
            .defined()
            .when('notAllowed', {
                is: (value: boolean | null) => value === true,
                then: schema =>
                    schema
                        .equals([0], 'mMst be 0 when not allowed is true')
                        .default(0),
                otherwise: schema => schema.positive().required(),
            }),
        upCharge: Yup.number()
            .defined()
            .when('notAllowed', {
                is: (value: boolean | null) => value === true,
                then: schema =>
                    schema
                        .equals([0], 'Must be 0 when not allowed is true')
                        .default(0),
                otherwise: schema => schema.positive().default(1).required(),
            }),
        isDefault: Yup.boolean()
            .nullable()
            .default(null)
            .when('notAllowed', {
                is: (value: boolean | null) => value === true,
                then: schema =>
                    schema
                        .equals(
                            [false],
                            'Must be false when not allowed is true'
                        )
                        .default(false),
                otherwise: schema => schema.default(null),
            }),
        displayOrder: Yup.number().nullable().default(null),
    });
export const CREATE_BUSINESS_POUR_AVAILABILITY_SCHEMA =
    BUSINESS_POUR_AVAILABILITY_SCHEMA.omit(['id']);

export const MENU_ITEM_SCHEMA = Yup.object<MenuItem.Base>().shape({
    id: Yup.number().required(),
    type: Yup.string()
        .oneOf<MenuItemType>(enumToList(MENU_ITEM_TYPES))
        .required(),
    businessId: Yup.number().required(),
    standardDrinkId: Yup.number()
        .nullable()
        .default(null)
        .when('type', {
            is: (type: string) => type === MENU_ITEM_TYPES.StandardDrink,
            then: schema => schema.required(),
        }),
    customDrinkId: Yup.number()
        .nullable()
        .default(null)
        .when('type', {
            is: (type: string) => type === MENU_ITEM_TYPES.CustomDrink,
            then: schema => schema.required(),
        }),
    productId: Yup.number()
        .nullable()
        .default(null)
        .when('type', {
            is: (type: string) => type === MENU_ITEM_TYPES.Product,
            then: schema => schema.required(),
        }),
    displayOrder: Yup.number().nullable().default(null),
    isActive: Yup.boolean().default(true),
    featuredItem: Yup.boolean().default(false),
    notes: Yup.string()
        .nullable()
        .transform(transformEmptyToNull)
        .default(null),
});
export const CREATE_MENU_ITEM_SCHEMA = MENU_ITEM_SCHEMA.omit(['id']);
